import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import Checkbox from "../../components/Checkbox";
import { colors } from "../../config/colors";
import { images } from "../../config/images";
import { useAuth } from "../../context/AuthContext";

import Swal from "sweetalert2";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterLogin = searchParams.get("redirect");
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError("");
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Manual validation for empty fields
    const newFieldErrors = { email: "", password: "" };
    let hasError = false;

    if (!formData.email.trim()) {
      newFieldErrors.email = "Email is required";
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newFieldErrors.email = "Email is not valid";
        hasError = true;
      }
    }

    if (!formData.password) {
      newFieldErrors.password = "Password is required";
      hasError = true;
    } else if (formData.password.length < 6) {
      newFieldErrors.password = "Password is not valid";
      hasError = true;
    }

    if (hasError) {
      setFieldErrors(newFieldErrors);
      return;
    }

    setFieldErrors({ email: "", password: "" });
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password, rememberMe);
      if (result.success) {
        if (result.user?.role === "admin") navigate("/admin");
        else if (
          redirectAfterLogin &&
          redirectAfterLogin.startsWith("/") &&
          !redirectAfterLogin.startsWith("//")
        ) {
          navigate(redirectAfterLogin);
        } else navigate("/");
      } else {
        const errorMsg = result.message || "Invalid email or password";
        setError(errorMsg);

        if (result.message === "User not found") {
          setFieldErrors((prev) => ({
            ...prev,
            email: "Email is not correct",
          }));
        } else if (result.message === "Incorrect password") {
          setFieldErrors((prev) => ({
            ...prev,
            password: "Password is not correct",
          }));
        }

        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: errorMsg,
          confirmButtonColor: colors.primary[500],
        });
      }
    } catch (_) {
      const errorMsg = "Something went wrong. Please try again.";
      setError(errorMsg);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
        confirmButtonColor: colors.primary[500],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 py-12 lg:py-5 4xl:py-24">
        <div className="w-full max-w-6xl 4xl:max-w-11xl grid lg:grid-cols-2 gap-8 lg:gap-12 4xl:gap-32 items-center">
          {/* Left Side - Branding & Illustration */}
          <div className="hidden lg:flex flex-col items-center justify-center relative">
            <div className="relative w-full max-w-lg 4xl:max-w-3xl">
              {/* Logo Section */}
              <div className="flex items-center gap-4 4xl:gap-8 mb-8 4xl:mb-16">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-16 4xl:h-28 w-auto"
                />
                <div>
                  <h1 className="text-4xl whitespace-nowrap 4xl:text-7xl font-bold text-primary-600">
                    Market Mavericks
                  </h1>
                  <p className="text-gray-600 text-sm 4xl:text-2xl">
                    Your Gateway to Stock Market Success
                  </p>
                </div>
              </div>

              {/* Animated Chart Illustration */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 4xl:p-16 border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary-200 rounded-full -ml-12 -mb-12 opacity-20"></div>

                <div className="relative z-10">
                  <h3 className="text-2xl 4xl:text-5xl font-bold text-gray-800 mb-2">
                    Start Your Trading Journey
                  </h3>
                  <p className="text-gray-600 4xl:text-2xl mb-6">
                    Join thousands of successful traders
                  </p>

                  {/* Chart SVG */}
                  <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-6 4xl:p-12 mb-6 4xl:mb-12">
                    <svg viewBox="0 0 400 200" className="w-full h-40 4xl:h-72">
                      {/* Grid lines */}
                      <line
                        x1="0"
                        y1="40"
                        x2="400"
                        y2="40"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="80"
                        x2="400"
                        y2="80"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="120"
                        x2="400"
                        y2="120"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="160"
                        x2="400"
                        y2="160"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />

                      {/* Chart line - upward trend */}
                      <polyline
                        points="20,160 60,140 100,120 140,100 180,80 220,60 260,50 300,40 340,35 380,30"
                        fill="none"
                        stroke={colors.primary[500]}
                        strokeWidth="3"
                        className="animate-dash"
                      />

                      {/* Data points */}
                      {[20, 60, 100, 140, 180, 220, 260, 300, 340, 380].map(
                        (x, i) => {
                          const y = 160 - (i * 12 + Math.random() * 5);
                          return (
                            <circle
                              key={i}
                              cx={x}
                              cy={y}
                              r="4"
                              fill={colors.primary[500]}
                              className="animate-pulse"
                              style={{ animationDelay: `${i * 0.1}s` }}
                            />
                          );
                        },
                      )}
                    </svg>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 4xl:gap-12">
                    <div className="text-center">
                      <div className="text-2xl 4xl:text-5xl font-bold text-primary-600">
                        2.5M+
                      </div>
                      <div className="text-xs 4xl:text-xl text-gray-600">
                        Followers
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl 4xl:text-5xl font-bold text-secondary-600">
                        100%
                      </div>
                      <div className="text-xs 4xl:text-xl text-gray-600">
                        Happy Clients
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl 4xl:text-5xl font-bold text-primary-600">
                        ₹50K+
                      </div>
                      <div className="text-xs 4xl:text-xl text-gray-600">
                        Avg Returns
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-xl 4xl:max-w-3xl mx-auto lg:mx-0">
            <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10 4xl:p-20 border border-gray-100">
              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center gap-3 mb-6">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-12 w-auto"
                />
                <h2 className="text-2xl font-bold text-primary-600">
                  Market Mavericks
                </h2>
              </div>

              <div className="mb-8 4xl:mb-16">
                <h2 className="text-3xl 4xl:text-6xl font-bold text-gray-900 mb-2">
                  Welcome
                </h2>
                <p className="text-gray-600 4xl:text-2xl">
                  Sign in to continue your trading journey
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="space-y-2 4xl:space-y-10"
                noValidate
              >
                <TextInput
                  name="email"
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  error={fieldErrors.email}
                  required
                />
                <TextInput
                  name="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  error={fieldErrors.password}
                  required
                />

                <div className="flex items-center justify-between">
                  <Checkbox
                    name="rememberMe"
                    label="Remember me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>

              <p className="mt-6 text-center text-gray-600">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Sign up here
                </Link>
              </p>

              <div className="mt-4 text-center">
                <Link
                  to="/"
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
